import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  BottomDrawer,
  FormField,
  Alert,
  Badge,
  Paper,
  Text,
  Select,
  NumberInput,
} from '@vipso/shared';
import {
  useListApprovalFlowDefinitionsQuery,
  useUpsertApprovalFlowDefinitionMutation,
  useListRolesQuery,
} from '../api/tenantApi';
import { useAccess } from '../hooks/useAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';
import { useModuleLabel } from '../hooks/useModuleLabel';

type LevelDraft = {
  key: string;
  roleId: string;
  minAmount: number;
};

type DefinitionRow = {
  id: string;
  moduleKey: string;
  isEnabled: boolean;
  levels?: Array<{
    id: string;
    levelOrder: number;
    roleId: string;
    roleName?: string | null;
    minAmount: number;
  }> | null;
};

const AMOUNT_MODULES = new Set(['PRODUCT', 'PROC_SALES', 'PROC_PURCHASE']);

const MODULE_ORDER = [
  'CUSTOMER',
  'VENDOR',
  'PRODUCT',
  'WAREHOUSE',
  'PROC_SALES',
  'PROC_PURCHASE',
] as const;

export function ApprovalFlowsPage() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();
  const canEditFlows = canMutate('roles.update');
  const approvalAccess = useApprovalModuleAccess();
  const customersLabel = useModuleLabel('customer', 'approvalFlows.modules.customer');
  const vendorsLabel = useModuleLabel('vendor', 'approvalFlows.modules.vendor');
  const productsLabel = useModuleLabel('product', 'approvalFlows.modules.product');


  function moduleAllowed(moduleKey: string): boolean {
    switch (moduleKey) {
      case 'CUSTOMER':
        return approvalAccess.customer;
      case 'VENDOR':
        return approvalAccess.vendor;
      case 'PRODUCT':
        return approvalAccess.product;
      case 'WAREHOUSE':
        return approvalAccess.warehouse;
      case 'PROC_SALES':
        return approvalAccess.procSales;
      case 'PROC_PURCHASE':
        return approvalAccess.procPurchase;
      default:
        return false;
    }
  }

  function moduleLabel(moduleKey: string): string {
    switch (moduleKey) {
      case 'CUSTOMER':
        return customersLabel;
      case 'VENDOR':
        return vendorsLabel;
      case 'PRODUCT':
        return productsLabel;
      case 'WAREHOUSE':
        return t('approvalFlows.modules.warehouse');
      case 'PROC_SALES':
        return t('approvalFlows.modules.sales');
      case 'PROC_PURCHASE':
        return t('approvalFlows.modules.purchase');
      default:
        return moduleKey;
    }
  }

  const { data, isLoading, isError, refetch } = useListApprovalFlowDefinitionsQuery();
  const { data: rolesData } = useListRolesQuery();
  const [upsert, { isLoading: saving }] = useUpsertApprovalFlowDefinitionMutation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<DefinitionRow | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [levels, setLevels] = useState<LevelDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  const roles = rolesData?.listRoles ?? [];
  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: String(r.id), label: r.name })),
    [roles],
  );

  // Only modules the admin has turned on — owners configure levels for those tiles.
  const rows = useMemo(() => {
    const list = data?.listApprovalFlowDefinitions ?? [];
    const byKey = new Map(list.map((d) => [d.moduleKey, d]));
    return MODULE_ORDER.filter((key) => moduleAllowed(key)).map((key) => {
      const existing = byKey.get(key);
      if (existing) return existing as DefinitionRow;
      return {
        id: `pending-${key}`,
        moduleKey: key,
        isEnabled: true,
        levels: [],
      } satisfies DefinitionRow;
    });
  }, [
    data?.listApprovalFlowDefinitions,
    approvalAccess.customer,
    approvalAccess.vendor,
    approvalAccess.product,
    approvalAccess.warehouse,
    approvalAccess.procSales,
    approvalAccess.procPurchase,
  ]);

  function openEdit(row: DefinitionRow) {
    setEditing(row);
    setEnabled(moduleAllowed(row.moduleKey));
    setLevels(
      (row.levels ?? [])
        .slice()
        .sort((a, b) => a.levelOrder - b.levelOrder)
        .map((l, idx) => ({
          key: l.id || `l-${idx}`,
          roleId: String(l.roleId),
          minAmount: Number(l.minAmount ?? 0),
        })),
    );
    setError(null);
    setDrawerOpen(true);
  }

  function addLevel() {
    setLevels((prev) => [
      ...prev,
      { key: `new-${Date.now()}`, roleId: '', minAmount: 0 },
    ]);
  }

  function removeLevel(key: string) {
    setLevels((prev) => prev.filter((l) => l.key !== key));
  }

  function moveLevel(index: number, dir: -1 | 1) {
    setLevels((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      const tmp = next[index]!;
      next[index] = next[j]!;
      next[j] = tmp;
      return next;
    });
  }

  async function handleSave() {
    if (!editing) return;
    setError(null);
    const allowed = moduleAllowed(editing.moduleKey);
    if (allowed && levels.length === 0) {
      setError(t('approvalFlows.validation.needLevel'));
      return;
    }
    if (allowed && levels.some((l) => !l.roleId)) {
      setError(t('approvalFlows.validation.needRole'));
      return;
    }
    try {
      await upsert({
        moduleKey: editing.moduleKey,
        isEnabled: allowed,
        levels: levels.map((l, idx) => ({
          levelOrder: idx + 1,
          roleId: l.roleId,
          minAmount: AMOUNT_MODULES.has(editing.moduleKey) ? Number(l.minAmount) || 0 : 0,
        })),
      }).unwrap();
      setDrawerOpen(false);
      await refetch();
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message ?? '')
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message ?? '')
            : '';
      setError(message || t('approvalFlows.saveFailed'));
    }
  }

  const showAmount = editing ? AMOUNT_MODULES.has(editing.moduleKey) : false;

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('approvalFlows.title')}
        description={t('approvalFlows.description')}
      />

      {isError ? <Alert color="red" title={t('approvalFlows.loadFailed')} /> : null}

      <Stack gap="md">
        {isLoading && !data ? (
          <Text c="dimmed">{t('common.loading')}</Text>
        ) : rows.length === 0 ? (
          <Alert color="gray" title={t('approvalFlows.noneEnabledTitle')}>
            {t('approvalFlows.noneEnabled')}
          </Alert>
        ) : (
          rows.map((row) => {
            const levelCount = row.levels?.length ?? 0;
            const amountBased = (row.levels ?? []).some((l) => Number(l.minAmount) > 0);
            const finalRole =
              levelCount > 0
                ? [...(row.levels ?? [])].sort((a, b) => a.levelOrder - b.levelOrder).at(-1)
                    ?.roleName
                : null;
            return (
              <Paper key={row.id} p="lg" withBorder radius="md">
                <Group justify="space-between" align="flex-start" wrap="wrap">
                  <Stack gap={4}>
                    <Group gap="sm">
                      <Text fw={600}>{moduleLabel(row.moduleKey)}</Text>
                      {amountBased ? (
                        <Badge size="sm" color="blue" variant="light">
                          {t('approvalFlows.amountBased')}
                        </Badge>
                      ) : null}
                    </Group>
                    <Text size="sm" c="dimmed">
                      {levelCount === 0
                        ? t('approvalFlows.noLevels')
                        : t('approvalFlows.levelSummary', {
                            count: levelCount,
                            final: finalRole || '—',
                          })}
                    </Text>
                  </Stack>
                  {canEditFlows ? (
                    <Button variant="light" onClick={() => openEdit(row)}>
                      {t('common.edit')}
                    </Button>
                  ) : null}
                </Group>
              </Paper>
            );
          })
        )}
      </Stack>

      <BottomDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={
          editing
            ? t('approvalFlows.editTitle', { module: moduleLabel(editing.moduleKey) })
            : t('approvalFlows.title')
        }
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('approvalFlows.autoApplyHint')}
          </Text>
          <FormField label={t('approvalFlows.enable')}>
            <Badge
              size="sm"
              color={enabled ? 'green' : 'gray'}
              variant="light"
            >
              {enabled ? t('approvalFlows.enabled') : t('approvalFlows.disabled')}
            </Badge>
            <Text size="xs" c="dimmed" mt={4}>
              {t('approvalFlows.adminManagedHint')}
            </Text>
          </FormField>

          <Group justify="space-between" align="center">
            <Text fw={600}>{t('approvalFlows.levels')}</Text>
            <Button size="xs" variant="light" onClick={addLevel}>
              {t('approvalFlows.addLevel')}
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            {t('approvalFlows.levelsHint')}
          </Text>

          {levels.map((level, index) => {
            const isFinal = index === levels.length - 1;
            return (
              <Paper key={level.key} p="md" withBorder radius="md">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text size="sm" fw={600}>
                      {t('approvalFlows.levelN', { n: index + 1 })}
                      {isFinal ? ` · ${t('approvalFlows.finalApproval')}` : ''}
                    </Text>
                    <Group gap={4}>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        disabled={index === 0}
                        onClick={() => moveLevel(index, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        disabled={index === levels.length - 1}
                        onClick={() => moveLevel(index, 1)}
                      >
                        ↓
                      </Button>
                      <Button
                        size="compact-xs"
                        color="red"
                        variant="subtle"
                        onClick={() => removeLevel(level.key)}
                      >
                        {t('common.delete')}
                      </Button>
                    </Group>
                  </Group>
                  <FormField label={t('approvalFlows.role')}>
                    <Select
                      data={roleOptions}
                      value={level.roleId || null}
                      onChange={(v) =>
                        setLevels((prev) =>
                          prev.map((l) =>
                            l.key === level.key ? { ...l, roleId: v || '' } : l,
                          ),
                        )
                      }
                      placeholder={t('approvalFlows.selectRole')}
                      searchable
                    />
                  </FormField>
                  {showAmount ? (
                    <FormField label={t('approvalFlows.minAmount')}>
                      <NumberInput
                        value={level.minAmount}
                        min={0}
                        onChange={(v) =>
                          setLevels((prev) =>
                            prev.map((l) =>
                              l.key === level.key
                                ? { ...l, minAmount: typeof v === 'number' ? v : 0 }
                                : l,
                            ),
                          )
                        }
                      />
                    </FormField>
                  ) : null}
                </Stack>
              </Paper>
            );
          })}

          {error ? <Alert color="red" title={error} /> : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDrawerOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={saving}
              disabled={!canEditFlows}
              onClick={() => void handleSave()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
